import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon,
  Bell,
  Shield,
  Database,
  Download,
  Trash2
} from 'lucide-react';

const Settings = () => {
  const { user, hasRole } = useAuth();
  const [notifications, setNotifications] = useState({
    email: true,
    browser: true,
    followup: true,
    assignments: true
  });

  const handleSaveNotifications = () => {
    toast.success('Impostazioni notifiche salvate');
  };

  if (!hasRole(['super_admin'])) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Accesso Negato</h2>
          <p className="text-slate-500 mt-2">Non hai i permessi per accedere a questa sezione.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Impostazioni</h1>
        <p className="text-slate-500 mt-1">Configura le impostazioni del sistema</p>
      </div>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifiche
          </CardTitle>
          <CardDescription>Gestisci le preferenze di notifica del sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Notifiche Email</p>
              <p className="text-sm text-slate-500">Ricevi notifiche via email</p>
            </div>
            <Switch
              checked={notifications.email}
              onCheckedChange={(checked) => setNotifications({...notifications, email: checked})}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Notifiche Browser</p>
              <p className="text-sm text-slate-500">Mostra notifiche nel browser</p>
            </div>
            <Switch
              checked={notifications.browser}
              onCheckedChange={(checked) => setNotifications({...notifications, browser: checked})}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Reminder Follow-up</p>
              <p className="text-sm text-slate-500">Promemoria per follow-up lead</p>
            </div>
            <Switch
              checked={notifications.followup}
              onCheckedChange={(checked) => setNotifications({...notifications, followup: checked})}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Nuove Assegnazioni</p>
              <p className="text-sm text-slate-500">Notifica quando vengono assegnati lead o pratiche</p>
            </div>
            <Switch
              checked={notifications.assignments}
              onCheckedChange={(checked) => setNotifications({...notifications, assignments: checked})}
            />
          </div>
          <div className="pt-4">
            <Button onClick={handleSaveNotifications}>
              Salva Preferenze
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Sicurezza
          </CardTitle>
          <CardDescription>Impostazioni di sicurezza dell'account</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Sessione Corrente</Label>
            <p className="text-sm text-slate-500">
              Ultimo accesso: {new Date().toLocaleString('it-IT')}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Autenticazione a Due Fattori</Label>
            <p className="text-sm text-slate-500">
              La 2FA non è ancora abilitata per questo account.
            </p>
            <Button variant="outline" disabled>
              Abilita 2FA (Prossimamente)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5" />
            Gestione Dati
          </CardTitle>
          <CardDescription>Esporta o gestisci i dati del sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Esporta Tutti i Dati</p>
              <p className="text-sm text-slate-500">Scarica un backup completo del database</p>
            </div>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Esporta
            </Button>
          </div>
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
            <div>
              <p className="font-medium text-red-900">Zona Pericolosa</p>
              <p className="text-sm text-red-600">Elimina tutti i dati demo dal sistema</p>
            </div>
            <Button variant="destructive" disabled>
              <Trash2 className="w-4 h-4 mr-2" />
              Reset DB
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" />
            Informazioni Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Versione App</p>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <p className="text-slate-500">Ambiente</p>
              <p className="font-medium">Production</p>
            </div>
            <div>
              <p className="text-slate-500">Database</p>
              <p className="font-medium">MongoDB</p>
            </div>
            <div>
              <p className="text-slate-500">API Version</p>
              <p className="font-medium">v1</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
